import React from 'react';
import { CalendarCheck, ClipboardList, Lock, Sparkles } from 'lucide-react';
import { Card, ScoreBar, SectionHeader } from '../../components/journey/ui';
import { DIMENSIONS } from './journeyApi';

const STAGES = {
  assessment: {
    icon: ClipboardList, eyebrow: 'Your next step',
    title: 'Take the free Career Readiness Check',
    text: 'Answer 12 quick questions to see where you stand on career direction, profile strength, interview readiness and job search. It takes about 3 minutes.',
  },
  booking: {
    icon: Sparkles, eyebrow: 'Your next step',
    title: 'Book your Career Clarity Consultation',
    text: 'A one-to-one session with an Elevate career advisor to turn your results into clear target roles and a Personalized Career Summary.',
  },
  booked: {
    icon: CalendarCheck, eyebrow: 'You\u2019re booked',
    title: 'Your Career Clarity Consultation is booked',
    text: 'An Elevate career advisor will reach out to schedule your session. The rest of your dashboard unlocks right after it.',
  },
};

const NextStep = ({ d }) => {
  const st = STAGES[d.stage];
  const Icon = st.icon;
  return (
    <Card className="bg-[#0b1f4d] text-white border-0" data-testid={`locked-next-step-${d.stage}`}>
      <Icon className="w-6 h-6 text-teal-300 mb-4" />
      <p className="text-[11px] uppercase tracking-[0.2em] text-teal-300">{st.eyebrow}</p>
      <p className="font-outfit text-2xl font-semibold mt-1">{st.title}</p>
      <p className="text-slate-300 text-sm mt-3 leading-relaxed">{st.text}</p>
      {d.stage === 'assessment' && <a href={d.readiness_link} data-testid="locked-take-assessment-btn" className="inline-block mt-6 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold text-sm px-6 py-3 rounded-full transition-colors">Take the Readiness Check</a>}
      {d.stage === 'booking' && <a href="/apply?plan=clarity-session" data-testid="locked-book-btn" className="inline-block mt-6 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold text-sm px-6 py-3 rounded-full transition-colors">Book Career Clarity · ₹499</a>}
    </Card>
  );
};

const UNLOCKS = ['Personalized Career Summary', 'Live career opportunities for your target roles', 'Learning journey and practical missions', 'Weekly check-ins with your coach'];

export const LockedOverview = ({ data: d }) => (
  <>
    <SectionHeader eyebrow="Your career journey" title={`Welcome, ${(d.student.full_name || '').split(' ')[0]}`}>
      {d.stage === 'assessment' ? 'Start with the Career Readiness Check. Everything else unlocks step by step.' : 'Here are your Career Readiness results. Your next step is a one-to-one consultation.'}
    </SectionHeader>
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 items-start" data-testid="locked-overview">
      <NextStep d={d} />
      {d.stage === 'assessment' ? (
        <Card data-testid="locked-unlocks">
          <p className="font-outfit font-semibold text-[#0b1f4d] mb-3">Unlocks after your consultation</p>
          <ul className="space-y-2.5 text-sm text-slate-600">{UNLOCKS.map((u) => <li key={u} className="flex items-center gap-2.5"><Lock className="w-3.5 h-3.5 text-slate-400" />{u}</li>)}</ul>
        </Card>
      ) : (
        <Card data-testid="locked-readiness">
          <p className="font-outfit font-semibold text-[#0b1f4d] mb-4">Your Career Readiness</p>
          <div className="space-y-4">{DIMENSIONS.map(([k, l]) => <ScoreBar key={k} label={l} value={d.student.readiness[k]} testid={`locked-readiness-${k}`} />)}</div>
          <p className="text-xs text-slate-500 mt-4">The rest of your dashboard unlocks after your consultation.</p>
        </Card>
      )}
    </div>
  </>
);

export const NoProfile = ({ onLogout }) => (
  <div className="min-h-screen bg-[#0b1f4d] text-white flex items-center px-6" data-testid="dashboard-no-profile">
    <div className="max-w-xl mx-auto w-full">
      <p className="text-xs tracking-[0.25em] uppercase text-teal-300 mb-6">Elevate Careers · Student dashboard</p>
      <h1 className="font-outfit text-3xl font-bold">We couldn't find your Talent Network profile.</h1>
      <p className="text-slate-300 mt-4">Sign in with the email you used to join the Elevate Talent Network or take the Career Readiness Check.</p>
      <a href="/network" className="inline-block mt-7 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] font-semibold px-7 py-3.5 rounded-full" data-testid="dashboard-no-profile-join">Join the Talent Network</a>
      <button onClick={onLogout} className="block mt-8 text-xs text-slate-400 hover:text-white" data-testid="locked-logout-btn">Sign out</button>
    </div>
  </div>
);
